import { merge } from 'merge-anything';
import { defaultConfig, SystemConfig } from '@chakra-ui/react';

import colors from './colors';
import dayPickerStyles from './dayPickerStyles';
import { themeRecipes } from './recipes';

export * from './recipes/slots';

const themeConfigOverrides: SystemConfig = {
  globalCss: {
    ...dayPickerStyles,

    'html, body, #app': {
      height: '100%',
    },
    '#app': {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'stretch',
      position: 'relative',
      zIndex: 1,
    },
    'body > *:not(#app)': {
      position: 'absolute',
      top: 0,
      left: 0,
      zIndex: -1,
    },
    option: {
      color: 'black',
    },
    ':root': {
      '--shimmer-rotate-angle': '0deg',
    },
  },
  theme: {
    ...themeRecipes,
    keyframes: {
      'shimmer-rotate': {
        to: {
          '--shimmer-rotate-angle': '360deg',
        },
      },
    },
    tokens: {
      ...colors,
      lineHeights: {
        shortest: { value: 1.14 },
      },
      gradients: {
        shimmer: {
          value:
            'linear-gradient(172deg, rgba(72, 196, 255, 0.10) 12.41%, rgba(144, 252, 200, 0.10) 55.9%, rgba(226, 151, 255, 0.10) 75.86%, rgba(52, 184, 255, 0.10) 100.84%)',
        },
        animatedShimmer: {
          value:
            'conic-gradient(from var(--shimmer-rotate-angle, 0deg), rgba(72, 196, 255, 0.80), rgba(144, 252, 200, 0.80), rgba(226, 151, 255, 0.80), rgba(52, 184, 255, 0.80))',
        },
      },
    },
    semanticTokens: {
      colors: {
        brandPrimary: {
          contrast: {
            value: {
              base: '{colors.brandPrimary.50}',
              _dark: '{colors.brandPrimaryDark.50}',
            },
          },
          fg: {
            value: {
              base: '{colors.brandPrimary.700}',
              _dark: '{colors.brandPrimaryDark.700}',
            },
          },
          subtle: {
            value: {
              base: '{colors.brandPrimary.100}',
              _dark: '{colors.brandPrimaryDark.100}',
            },
          },
          muted: {
            value: {
              base: '{colors.brandPrimary.200}',
              _dark: '{colors.brandPrimaryDark.200}',
            },
          },
          emphasized: {
            value: {
              base: '{colors.brandPrimary.300}',
              _dark: '{colors.brandPrimaryDark.300}',
            },
          },
          solid: {
            value: {
              base: '{colors.brandPrimary.600}',
              _dark: '{colors.brandPrimaryDark.600}',
            },
          },
          focusRing: {
            value: {
              base: '{colors.brandPrimary.500}',
              _dark: '{colors.brandPrimaryDark.500}',
            },
          },
        },
        brandSecondary: {
          contrast: {
            value: {
              base: '{colors.brandSecondary.50}',
              _dark: '{colors.brandSecondaryDark.50}',
            },
          },
          fg: {
            value: {
              base: '{colors.brandSecondary.700}',
              _dark: '{colors.brandSecondaryDark.700}',
            },
          },
          subtle: {
            value: {
              base: '{colors.brandSecondary.100}',
              _dark: '{colors.brandSecondaryDark.100}',
            },
          },
          muted: {
            value: {
              base: '{colors.brandSecondary.200}',
              _dark: '{colors.brandSecondaryDark.200}',
            },
          },
          emphasized: {
            value: {
              base: '{colors.brandSecondary.300}',
              _dark: '{colors.brandSecondaryDark.300}',
            },
          },
          solid: {
            value: {
              base: '{colors.brandSecondary.600}',
              _dark: '{colors.brandSecondaryDark.600}',
            },
          },
          focusRing: {
            value: {
              base: '{colors.brandSecondary.500}',
              _dark: '{colors.brandSecondaryDark.500}',
            },
          },
        },
        surface: {
          bg: {
            value: {
              base: '{colors.brandSurface.bg}',
              _dark: '{colors.brandSurfaceDark.bg}',
            },
          },
          deepRecess: {
            value: {
              base: '{colors.brandSurface.deepRecess}',
              _dark: '{colors.brandSurfaceDark.deepRecess}',
            },
          },
          recessed: {
            value: {
              base: '{colors.brandSurface.recessed}',
              _dark: '{colors.brandSurfaceDark.recessed}',
            },
          },
          DEFAULT: {
            value: { base: '#ffffff', _dark: '#111c29' },
          },
          elevated: {
            value: { base: '#ffffff', _dark: '#1a2d40' },
          },
          field: {
            value: { base: '#ffffff', _dark: '#0c121c' },
          },
        },
        border: {
          brandedAccent: {
            value: { base: '{colors.tertiary.500}' },
          },
          brandedDefault: {
            value: {
              base: '{colors.brandBorder.branded}',
              _dark: '{colors.brandBorderDark.branded}',
            },
          },
          brandedSubtle: {
            value: {
              base: '{colors.brandBorder.brandedSubtle}',
              _dark: '{colors.brandBorderDark.brandedSubtle}',
            },
          },
        },
        text: {
          fg: {
            value: {
              base: '{colors.brandText.fg}',
              _dark: '{colors.brandTextDark.fg}',
            },
          },
        },
      },
      shadows: {
        md: {
          value: {
            _light: `0 4px 6px -1px var(--chakra-colors-black-alpha-200), 0 2px 4px -1px var(--chakra-colors-black-alpha-100)`,
            _dark: `0 4px 6px -1px var(--chakra-colors-black-alpha-200), 0 2px 4px -1px var(--chakra-colors-black-alpha-100)`,
          },
        },
        lg: {
          value: {
            _light: `0 4px 6px -2px var(--chakra-colors-black-alpha-100), 0 10px 15px -3px var(--chakra-colors-black-alpha-200)`,
            _dark: `0 4px 6px -2px var(--chakra-colors-black-alpha-100), 0 10px 15px -3px var(--chakra-colors-black-alpha-200)`,
          },
        },
      },
    },
    textStyles: {
      pageTitle: {
        value: {
          fontSize: '3xl',
          lineHeight: '2.375rem',
          fontWeight: 'bold',
        },
      },
      keyInformation: {
        value: {
          fontSize: 'md',
          lineHeight: '1.5rem',
          fontWeight: 'semibold',
        },
      },
      descriptionText: {
        value: {
          fontSize: 'sm',
          lineHeight: '1.25rem',
        },
      },
      header: {
        value: {
          fontSize: 'xl',
          lineHeight: '1.75rem',
        },
      },
      caption: {
        value: {
          fontSize: 'xs',
          lineHeight: '1rem',
        },
      },
    },
  },
};

export const themeConfig = merge<SystemConfig, SystemConfig[]>(
  defaultConfig, // this is the ONLY config that should import the defaultConfig
  themeConfigOverrides,
);
